import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ArrowLeft, RefreshCw } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";

export default function ResendVerificationPage() {
  const { resendVerification, isLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setResendError("Please enter your email address");
      return;
    }

    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      await resendVerification(email);
      setResendSuccess(true);
      setEmail(""); // Clear email after successful send
    } catch (error) {
      setResendError(
        error instanceof Error
          ? error.message
          : "Failed to resend verification email"
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout
      title="Resend Email Verification"
      subtitle="Enter your email to receive a new verification link"
      leftContent={{
        title: "Email",
        subtitle: "Verification",
        description:
          "Enter your email address to receive a new verification link. Check your inbox and spam folder after requesting.",
        trustIndicator: "Secure & encrypted communication",
      }}
    >
      <div className="space-y-6">
        {/* Success Message */}
        {resendSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-green-700 text-sm">
                  If this email exists in our system, you will receive a new
                  verification link.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {resendError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{resendError}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleResendVerification} className="space-y-6">
          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="email"
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isResending || isLoading}
                required
                className="pl-10 h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isResending || isLoading || !email}
            className="w-full h-12 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium rounded-lg transition-colors"
          >
            {isResending ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Sending Verification Link...
              </>
            ) : (
              "Send Verification Link"
            )}
          </Button>
        </form>

        {/* Back to Sign In */}
        <div className="text-center">
          <Link
            to="/signin"
            className="inline-flex items-center text-purple-600 hover:text-purple-500 font-medium text-sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sign In
          </Link>
        </div>

        {/* Additional Links */}
        <div className="text-center text-sm text-gray-600">
          <p>
            Already verified?{" "}
            <Link
              to="/signin"
              className="font-medium text-purple-600 hover:text-purple-500"
            >
              Sign in
            </Link>
            {" • "}
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-medium text-purple-600 hover:text-purple-500"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
